import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Copy, Check } from 'lucide-react'
import type { ChatMessage } from '../../shared/types/chat'
import { BottomSheet } from '@tendbot/ui'

interface Props {
  isOpen: boolean
  onClose: () => void
  conversationId: string | null
  messages: ChatMessage[]
}

function formatTime(ts: string): string {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  } catch {
    return ts
  }
}

function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { /* clipboard unavailable */ }
  }, [text])

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 px-2 py-1 rounded text-xs text-pearl/50 hover:text-gold hover:bg-gold/10 transition-colors"
    >
      {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
      {label}
    </button>
  )
}

export default function DebugModal({ isOpen, onClose, conversationId, messages }: Props) {
  const { t } = useTranslation('debug')
  const last5 = messages.slice(-5)

  const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost'

  const contextCommand = useCallback(() => {
    if (!conversationId) return ''
    if (isLocalhost) {
      return `grep '${conversationId}' ~/.pm2/logs/api-out.log | grep 'Context assembled' | tail -1 | node -e "process.stdin.on('data',d=>{try{const o=JSON.parse(d);console.log(o.contextBlock||'(none)')}catch{console.log('parse error')}})"`
    }
    return `['tendbot-logs'] | where ['conversationId'] == "${conversationId}" and ['msg'] == "[gateway] Context assembled" | sort by ['_time'] desc | take 1 | project ['_time'], ['contextBlock']`
  }, [conversationId, isLocalhost])

  const formatAll = useCallback(() => {
    const lines = [
      `URL: ${window.location.href}`,
      `Conversation: ${conversationId ?? t('none')}`,
      '',
    ]
    last5.forEach((m) => {
      lines.push(`[${m.type.toUpperCase()}] ${m.id}  ${formatTime(m.timestamp)}`)
      lines.push(m.content || t('noContent'))
      lines.push('')
    })
    return lines.join('\n')
  }, [conversationId, last5, t])

  return (
    <BottomSheet
      open={isOpen}
      onOpenChange={(o) => { if (!o) onClose() }}
      title={t('title')}
      className="max-h-[calc(100vh-3rem)]"
    >
      <div className="px-5 py-4 border-b border-white/10 shrink-0">
        <h2 className="font-display text-ivory text-base font-semibold">{t('title')}</h2>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        <section>
          <h3 className="text-pearl/60 text-xs font-medium uppercase tracking-wider mb-2">{t('section.conversation')}</h3>
          <div className="glass-card rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-pearl/50 text-xs">{t('label.id')}</span>
              <div className="flex items-center gap-1">
                <span className="text-ivory text-xs font-mono">{conversationId ?? t('none')}</span>
                {conversationId && <CopyButton text={conversationId} />}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-pearl/50 text-xs">{t('label.messages')}</span>
              <span className="text-ivory text-xs font-mono">{messages.length}</span>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-pearl/60 text-xs font-medium uppercase tracking-wider mb-2">
            {t('label.lastN', { count: last5.length })}
          </h3>
          <div className="space-y-2">
            {last5.length === 0 && (
              <p className="text-pearl/55 text-xs italic">{t('empty')}</p>
            )}
            {last5.map((msg) => (
              <div key={msg.id} className="glass-card rounded-lg p-3 space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                        msg.type === 'user'
                          ? 'bg-blue-500/20 text-blue-300'
                          : 'bg-gold/20 text-gold'
                      }`}
                    >
                      {msg.type === 'user' ? t('badge.user') : t('badge.ai')}
                    </span>
                    <span className="text-pearl/60 text-[10px] font-mono">{formatTime(msg.timestamp)}</span>
                  </div>
                  <CopyButton text={msg.content || t('noContent')} />
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-pearl/55 text-[10px] font-mono shrink-0 pt-0.5">{msg.id.slice(0, 8)}</span>
                  <p className="text-ivory/80 text-xs leading-relaxed break-all line-clamp-4">
                    {msg.content ? msg.content.slice(0, 200) : '(no text content)'}
                    {msg.content && msg.content.length > 200 && '...'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="pb-4 flex gap-2">
          <CopyButton text={formatAll()} label={t('action.copyAll')} />
          {conversationId && (
            <CopyButton text={contextCommand()} label={t('action.copyContext')} />
          )}
        </section>
      </div>
    </BottomSheet>
  )
}
